import { Box, ChevronLeft, ChevronRight, Copy, FileText, Layers, LayoutGrid, Leaf, Barcode } from 'lucide-react';
import type { InventoryDetailItem } from '../types/inventoryDetail.types';
import { InventoryDetailStatusBadge } from './InventoryDetailStatusBadge';
import { Edit3 } from 'lucide-react';

interface InventoryDetailInfoProps {
  item: InventoryDetailItem;
  stockValue: number;
}

export function InventoryDetailInfo({ item, stockValue }: InventoryDetailInfoProps) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
      <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr_340px] gap-10">
        {/* Column 1: Image Gallery */}
        <div className="space-y-4">
          <div className="aspect-square w-full overflow-hidden rounded-md border border-slate-100 bg-slate-50 flex items-center justify-center text-emerald-600">
            {item.thumbnail_url ? (
              <img src={item.thumbnail_url} alt={item.name} className="h-full w-full object-cover" />
            ) : (
              <Leaf className="h-20 w-20 opacity-20" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <button className="flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-400 hover:text-slate-600 transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex flex-1 gap-2 overflow-hidden justify-center">
              <div className="h-16 w-16 shrink-0 rounded-md border-2 border-emerald-500 bg-slate-50 p-0.5 overflow-hidden">
                {item.thumbnail_url ? <img src={item.thumbnail_url} alt="" className="h-full w-full rounded object-cover" /> : <div className="h-full w-full bg-slate-100" />}
              </div>
              <div className="h-16 w-16 shrink-0 rounded-md border border-slate-100 bg-slate-50 overflow-hidden">
                <div className="h-full w-full bg-slate-100 flex items-center justify-center"><Leaf className="h-6 w-6 opacity-10" /></div>
              </div>
              <div className="h-16 w-16 shrink-0 rounded-md border border-slate-100 bg-slate-50 overflow-hidden">
                <div className="h-full w-full bg-slate-100 flex items-center justify-center"><Leaf className="h-6 w-6 opacity-10" /></div>
              </div>
            </div>
            <button className="flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-400 hover:text-slate-600 transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Column 2: Core Info */}
        <div className="space-y-6">
          <div className="space-y-1">
            <div className="inline-flex rounded-sm bg-emerald-50 px-2 py-0.5 text-emerald-600 tracking-wider text-[10px] font-bold">
              {item.code}
            </div>
            <h2 className="sm:text-sm text-xs text-slate-900 leading-tight font-semibold">{item.name}</h2>
            {item.origin_name && <p className="sm:text-sm text-xs text-slate-400 italic">{item.origin_name}</p>}
          </div>

          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-[140px_1fr] gap-4 items-start">
              <div className="flex items-center gap-2 text-slate-400">
                <LayoutGrid className="h-4 w-4" />
                <span className="sm:text-sm text-xs">Danh mục</span>
              </div>
              <div className="sm:text-sm text-xs text-slate-700 flex items-center gap-2">
                <span className="font-medium">{item.category_name || 'Chưa phân loại'}</span>
                {item.category_name && <><ChevronRight className="h-3 w-3 text-slate-300" /> <span className="text-slate-400 italic">Chi tiết</span></>}
              </div>
            </div>

            <div className="grid grid-cols-[140px_1fr] gap-4 items-center">
              <div className="flex items-center gap-2 text-slate-400">
                <Box className="h-4 w-4" />
                <span className="sm:text-sm text-xs">Đơn vị tính</span>
              </div>
              <span className="sm:text-sm text-xs text-slate-700 font-medium">{item.unit}</span>
            </div>

            <div className="grid grid-cols-[140px_1fr] gap-4 items-center">
              <div className="flex items-center gap-2 text-slate-400">
                <Layers className="h-4 w-4" />
                <span className="sm:text-sm text-xs">Mã nhóm</span>
              </div>
              <span className="sm:text-sm text-xs text-slate-700 font-medium">{item.manufacturer_code || '-'}</span>
            </div>

            <div className="grid grid-cols-[140px_1fr] gap-4 items-center">
              <div className="flex items-center gap-2 text-slate-400">
                <Barcode className="h-4 w-4" />
                <span className="sm:text-sm text-xs">Mã cây</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="sm:text-sm text-xs text-slate-700 font-medium">{item.code}</span>
                <button className="text-slate-300 hover:text-emerald-600 transition-colors">
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-[140px_1fr] gap-4 items-start pt-3 border-t border-slate-50">
              <div className="flex items-center gap-2 text-slate-400 mt-0.5">
                <FileText className="h-4 w-4" />
                <span className="sm:text-sm text-xs">Mô tả</span>
              </div>
              <p className="sm:text-sm text-xs leading-relaxed text-slate-500">
                {item.note || 'Chưa có mô tả chi tiết cho vật tư này.'}
              </p>
            </div>
          </div>
        </div>

        {/* Column 3: Stats Summary */}
        <div className="rounded-md border border-slate-100 bg-slate-50/40 p-5 space-y-6 self-start">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 tracking-widest uppercase">Trạng thái</span>
            <InventoryDetailStatusBadge status={item.status_code} />
          </div>

          <div className="grid grid-cols-2 gap-4 border-b border-slate-100 pb-6">
            <div className="space-y-1">
              <p className="sm:text-sm text-xs text-slate-400">Tồn hiện tại</p>
              <div className="flex items-baseline gap-1">
                <span className="sm:text-sm text-xs text-emerald-600 font-bold">{item.current_stock.toLocaleString('vi-VN')}</span>
                <span className="text-xs text-slate-400">{item.unit}</span>
              </div>
            </div>
            <div className="space-y-1">
              <p className="sm:text-sm text-xs text-slate-400">Giá trị tồn</p>
              <p className="sm:text-sm text-xs text-slate-900 font-bold">{stockValue.toLocaleString('vi-VN')} đ</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 border-b border-slate-100 pb-6 relative group">
            <div className="space-y-1">
              <p className="sm:text-sm text-xs text-slate-400">Giá bán đề xuất</p>
              <p className="sm:text-sm text-xs text-slate-900 font-bold mt-1">
                {item.unit_price.toLocaleString('vi-VN')} đ <span className="text-xs text-slate-400 font-normal">/{item.unit}</span>
              </p>
            </div>
            <div className="space-y-1">
              <p className="sm:text-sm text-xs text-slate-400">Giá vốn đề xuất</p>
              <p className="sm:text-sm text-xs text-slate-900 font-bold mt-1">
                {(item.unit_price * 0.8).toLocaleString('vi-VN')} đ <span className="text-xs text-slate-400 font-normal">/{item.unit}</span>
              </p>
            </div>
            <button className="absolute -right-1 top-0 text-slate-300 hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity">
              <Edit3 className="h-3 w-3" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-1">
              <p className="sm:text-sm text-xs text-slate-400 tracking-tighter">Ngày tạo</p>
              <p className="sm:text-sm text-xs text-slate-600 font-medium">
                {item.created_at ? new Date(item.created_at).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="sm:text-sm text-xs text-slate-400 tracking-tighter">Cập nhật lần cuối</p>
              <p className="sm:text-sm text-xs text-slate-600 font-medium">
                {item.updated_at ? new Date(item.updated_at).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
